import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  MenuItem,
  Select,
  InputAdornment,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard";
import UnsavedChangesBar from "../shared/UnsavedChangesBar";
import UsPhoneField, { toPhoneDigits, toE164 } from "../shared/UsPhoneField";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Phoenix", label: "Mountain Time - Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "America/Puerto_Rico", label: "Atlantic Time (Puerto Rico)" },
  { value: "Pacific/Guam", label: "Chamorro Time (Guam)" },
];

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
  "PR",
  "GU",
  "VI",
  "AS",
  "MP",
];


const FieldLabel = ({ children }) => (
  <Typography variant="subtitle2" sx={{ mb: 1 }}>
    {children}
  </Typography>
);

const SectionHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Fields edited as free text — these accumulate in the form until the user hits
// Save on the sticky bar. Switches and the timezone select auto-save instead.
const MANUAL_SAVE_FIELDS = [
  "name",
  "phone",
  "email",
  "address_line1",
  "address_line2",
  "city",
  "state",
  "zip",
  "cancellation_window_hours",
  "auto_complete_after_minutes",
];

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null); // { message, severity }

  // Snapshot of what's persisted server-side. Auto-saves merge one field into
  // this (never the live form) so half-typed text is never persisted.
  const [savedForm, setSavedForm] = useState(null);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    timezone: "America/New_York",
    cancellation_window_hours: 24,
    fadepilot_fee_rate: 0.025,
    sms_notifications_enabled: false,
    email_notifications_enabled: false,
    push_notifications_enabled: true,
    barber_push_notifications_enabled: true,
    allow_barber_edit_schedule: true,
    allow_barber_edit_services: true,
    auto_complete_after_minutes: 60,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api("/tenants/settings.php", { method: "GET" });
        const s = res.data.settings;
        const loaded = {
          name: s.name ?? "",
          phone: toPhoneDigits(s.phone),
          email: s.email ?? "",
          address_line1: s.address_line1 ?? "",
          address_line2: s.address_line2 ?? "",
          city: s.city ?? "",
          state: s.state ?? "",
          zip: s.zip ?? "",
          timezone: s.timezone ?? "America/New_York",
          cancellation_window_hours: s.cancellation_window_hours ?? 24,
          fadepilot_fee_rate: s.fadepilot_fee_rate ?? 0.025,
          sms_notifications_enabled: s.sms_notifications_enabled ?? false,
          email_notifications_enabled: s.email_notifications_enabled ?? false,
          push_notifications_enabled: s.push_notifications_enabled ?? true,
          barber_push_notifications_enabled: s.barber_push_notifications_enabled ?? true,
          allow_barber_edit_schedule: s.allow_barber_edit_schedule ?? true,
          allow_barber_edit_services: s.allow_barber_edit_services ?? true,
          auto_complete_after_minutes: s.auto_complete_after_minutes ?? 60,
        };
        setForm(loaded);
        setSavedForm(loaded);
      } catch (e) {
        setError(e.message || "Failed to load settings.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Dirty = a manual-save (text) field differs from what's persisted. Auto-saved
  // fields (switches, timezone) are excluded so the bar doesn't flash during
  // their round-trip.
  const dirty = useMemo(
    () =>
      !!savedForm &&
      MANUAL_SAVE_FIELDS.some((k) => String(form[k]) !== String(savedForm[k])),
    [form, savedForm],
  );

  useUnsavedChangesGuard(dirty);

  const putSettings = async (values) => {
    await api("/tenants/settings.php", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...values,
        phone: toE164(values.phone),
        cancellation_window_hours: parseInt(
          values.cancellation_window_hours,
          10,
        ),
        fadepilot_fee_rate: parseFloat(values.fadepilot_fee_rate),
        auto_complete_after_minutes: parseInt(
          values.auto_complete_after_minutes,
          10,
        ),
      }),
    });
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Auto-save a single switch/select: merge the new value into the last-saved
  // snapshot (NOT the live form) so in-progress text edits are never persisted.
  const autoSave = async (field, value) => {
    if (!savedForm) return; // initial load failed; nothing safe to merge into
    setForm((prev) => ({ ...prev, [field]: value }));
    const next = { ...savedForm, [field]: value };
    try {
      await putSettings(next);
      setSavedForm(next);
      setSnack({ message: "Saved", severity: "success" });
    } catch (e) {
      // Revert the control so the UI never shows a state the server rejected.
      setForm((prev) => ({ ...prev, [field]: savedForm[field] }));
      setSnack({
        message: e.message || "Failed to save. Please try again.",
        severity: "error",
      });
    }
  };

  const handleToggle = (field) => (e) => autoSave(field, e.target.checked);
  const handleAutoSaveChange = (field) => (e) => autoSave(field, e.target.value);

  const handleSave = async () => {
    setSaving(true);
    try {
      await putSettings(form);
      setSavedForm(form);
      setSnack({ message: "Settings saved.", severity: "success" });
    } catch (e) {
      setSnack({
        message: e.message || "Failed to save settings.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setForm(savedForm);
  };

  if (loading) {
    return (
      <Box sx={{ p: 5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress size={18} sx={{ color: "text.secondary" }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading settings...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 3, md: 5 }, py: 5 }}>
      {/* ── Page header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h4" : "h3"} sx={{ mb: 0.5 }}>
            Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Manage your shop information and preferences. Toggles save
            automatically.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Shop Info ── */}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <SectionHeader
          title="Shop Information"
          subtitle="Basic details about your barbershop."
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Shop name */}
          <Box>
            <FieldLabel>Shop Name</FieldLabel>
            <TextField
              variant="outlined"
              label=""
              fullWidth
              placeholder="Enter your shop name"
              value={form.name}
              onChange={handleChange("name")}
            />
          </Box>

          {/* Phone + Email */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FieldLabel>Phone Number</FieldLabel>
              <UsPhoneField
                value={form.phone}
                onChange={(digits) =>
                  setForm((prev) => ({ ...prev, phone: digits }))
                }
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FieldLabel>Email Address</FieldLabel>
              <TextField
                variant="outlined"
                label=""
                fullWidth
                placeholder="Enter your email"
                value={form.email}
                onChange={handleChange("email")}
                />
            </Box>
          </Box>

          {/* Street Address */}
          <Box>
            <FieldLabel>Street Address</FieldLabel>
            <TextField
              variant="outlined"
              label=""
              fullWidth
              placeholder="e.g. 320 Howe Ave"
              value={form.address_line1}
              onChange={handleChange("address_line1")}
            />
          </Box>

          {/* Suite / Unit */}
          <Box>
            <FieldLabel>
              Suite / Unit{" "}
              <Typography
                component="span"
                variant="caption"
                sx={{ color: "text.secondary" }}
              >
                (optional)
              </Typography>
            </FieldLabel>
            <TextField
              variant="outlined"
              label=""
              fullWidth
              placeholder="e.g. Retail 2, Suite 100"
              value={form.address_line2}
              onChange={handleChange("address_line2")}
            />
          </Box>

          {/* City / State / ZIP */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 2, minWidth: 160 }}>
              <FieldLabel>City</FieldLabel>
              <TextField
                variant="outlined"
                label=""
                fullWidth
                placeholder="e.g. Shelton"
                value={form.city}
                onChange={handleChange("city")}
                />
            </Box>
            <Box sx={{ flex: 1, minWidth: 100 }}>
              <FieldLabel>State</FieldLabel>
              <Select
                fullWidth
                value={form.state}
                onChange={handleChange("state")}
                displayEmpty
                    renderValue={(v) =>
                  v || (
                    <Typography sx={{ color: "text.disabled" }}>
                      State
                    </Typography>
                  )
                }
              >
                {US_STATES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ flex: 1, minWidth: 100 }}>
              <FieldLabel>ZIP Code</FieldLabel>
              <TextField
                variant="outlined"
                label=""
                fullWidth
                placeholder="e.g. 06484"
                value={form.zip}
                onChange={handleChange("zip")}
                slotProps={{ htmlInput: { maxLength: 10 } }}
                />
            </Box>
          </Box>

          {/* Preview on Google Maps */}
          {(form.address_line1 || form.city) && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography
                variant="body2"
                onClick={() => {
                  const parts = [
                    form.address_line1,
                    form.address_line2,
                    form.city,
                    form.state,
                    form.zip,
                  ].filter(Boolean);
                  const query = encodeURIComponent(parts.join(", "));
                  window.open(
                    `https://www.google.com/maps?q=${query}`,
                    "_blank",
                  );
                }}
                sx={{
                  color: "text.third",
                  fontWeight: 500,
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  "&:hover": {
                    textDecoration: "underline",
                    textUnderlineOffset: "4px",
                  },
                }}
              >
                Preview on Google Maps →
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Timezone ── */}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <SectionHeader
          title="Timezone"
          subtitle="All appointment times will be displayed in this timezone."
        />
        <Box>
          <FieldLabel>Timezone</FieldLabel>
          <Select
            fullWidth
            value={form.timezone}
            onChange={handleAutoSaveChange("timezone")}
            displayEmpty
          >
            {TIMEZONES.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* ── Cancellation Policy ── */}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <SectionHeader
          title="Cancellation Policy"
          subtitle="Clients who cancel within the window will be charged the full service fee."
        />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <FieldLabel>Cancellation Window</FieldLabel>
            <TextField
              variant="outlined"
              label=""
              fullWidth
              type="number"
              placeholder="24"
              value={form.cancellation_window_hours}
              onChange={handleChange("cancellation_window_hours")}
              inputProps={{ min: 1, max: 168 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">hours</InputAdornment>
                ),
              }}
              helperText="Hours before the appointment the free cancellation window closes."
            />
          </Box>
        </Box>
      </Box>

      {/* ── Appointment Automation ── */}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <SectionHeader
          title="Appointment Automation"
          subtitle="Confirmed appointments are automatically marked completed after this delay once they end."
        />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <FieldLabel>Auto-complete delay</FieldLabel>
            <TextField
              variant="outlined"
              label=""
              fullWidth
              type="number"
              placeholder="60"
              value={form.auto_complete_after_minutes}
              onChange={handleChange("auto_complete_after_minutes")}
              inputProps={{ min: 0, max: 1440 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">minutes</InputAdornment>
                ),
              }}
              helperText="Minutes after an appointment ends before it's auto-marked completed. Set 0 to complete as soon as it ends."
            />
          </Box>
        </Box>
      </Box>

      {/* ── Client Notifications ── */}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <SectionHeader
          title="Client Notifications"
          subtitle="How clients are notified about their bookings."
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.sms_notifications_enabled}
                onChange={handleToggle("sms_notifications_enabled")}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  SMS Notifications
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Booking confirmations and reminders by text message.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", py: 1 }}
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={form.email_notifications_enabled}
                onChange={handleToggle("email_notifications_enabled")}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Email Notifications
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Booking confirmations and reminders by email.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", py: 1 }}
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={form.push_notifications_enabled}
                onChange={handleToggle("push_notifications_enabled")}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Push Notifications
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Booking updates to clients' phones via the mobile app.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", py: 1 }}
          />
        </Box>
      </Box>

      {/* ── Barber Notifications ── */}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <SectionHeader
          title="Barber Notifications"
          subtitle="How barbers are alerted about their schedule."
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.barber_push_notifications_enabled}
                onChange={handleToggle("barber_push_notifications_enabled")}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Push Notifications
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  New-booking and cancellation alerts to barbers' phones.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", py: 1 }}
          />
        </Box>
      </Box>

      {/* ── Staff Permissions ── */}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
        }}
      >
        <SectionHeader
          title="Staff Permissions"
          subtitle="Control what your barbers can manage themselves. When turned off, you manage it for them and their page becomes read-only."
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.allow_barber_edit_schedule}
                onChange={handleToggle("allow_barber_edit_schedule")}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Allow barbers to edit their schedule
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Barbers can set their own weekly working days and hours.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", py: 1 }}
          />
          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={form.allow_barber_edit_services}
                onChange={handleToggle("allow_barber_edit_services")}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Allow barbers to edit their services
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Barbers can toggle services on/off and adjust their own pricing
                  and duration.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", py: 1 }}
          />
        </Box>
      </Box>

      {/* ── Sticky unsaved-changes bar ── */}
      <UnsavedChangesBar
        open={dirty}
        saving={saving}
        onDiscard={handleDiscard}
        onSave={handleSave}
      />

      {/* ── Save feedback (auto-save + manual) ── */}
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack?.severity || "success"}
          variant="filled"
          onClose={() => setSnack(null)}
          sx={{ minWidth: 200 }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
